<template>
  <Banner :backgroundImage="$maxxpm.config.availability.banner.image"
    :backgroundVideo="$maxxpm.config.availability.banner.video" class="pt-6 pb-6 text-center">
    <h1 class="text-white display-1">Available Units</h1>
    <p class="text-white display-7 mt-4">Unit availability is updated often from Monday-Friday during business hours.
      Evenings and weekends may not reflect accurate availability dates.</p>
  </Banner>
  <div class="grid container">
    <div class="col-12 text-center" v-if="loadingUnits">
      <i class="pi pi-spin pi-spinner units-loading"></i>
    </div>
    <div class="col-12" v-else-if="$maxxpm.config.availability.hasWaitingList && !postedUnits.length">
      <p class="text-center text-xl mb-0">Sorry, there are no available units at this time.</p>
      <iframe :id="`JotFormIFrame-${$maxxpm.config.availability.waitingListJotformId}`" title="Vista View Waiting List"
        onload="window.parent.scrollTo(0,0)" allowtransparency="true" allowfullscreen="true"
        allow="geolocation; microphone; camera"
        :src="`https://form.jotform.com/${$maxxpm.config.availability.waitingListJotformId}`" frameborder="0"
        style="min-width: 100%; max-width: 100%; height: 539px; border: none" scrolling="no">
      </iframe>
    </div>
    <div class="listings-container col-12" v-else>
      <div v-for="unit in postedUnits" :key="unit.id" class="listing-card">
        <div class="listing-card-top">
          <div v-if="unit.promotions?.length" class="promotion-banner">
            <p class="promotion-banner-title">Limited Time Offer</p>
            <div v-for="promotion in unit.promotions" :key="promotion.id" class="promotion-banner-item">
              <i class="pi pi-megaphone promotion-icon"></i>
              <p>{{ promotion.description }}</p>
            </div>
          </div>
          <div v-else class="listing-card-top-spacer" aria-hidden="true"></div>
        </div>
        <UnitListing :listing="unit" source="abingtonheightstownhomes.com" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, onBeforeMount } from "vue";
import { UnitListing, Banner } from "@maxxpm/vue-components";
import axios from "axios";

const $maxxpm = inject("$maxxpm");

const postedUnits = ref([]);
const loadingUnits = ref(true);

const getListings = async () => {
  const apiUrl = process.env.VUE_APP_LISTINGS_API_URL;
  if (!apiUrl) return [];

  try {
    const posted = await axios.get(
      `${apiUrl}/listings?property_group=${$maxxpm.config.availability.propertyGroup}`
    );

    return posted.data.map((listing) => {
      let showingUrl = listing.showingUrl ? listing.showingUrl : listing.community.showingUrl ?? null;
      let virtualShowingUrl = listing.virtualShowingUrl ? listing.virtualShowingUrl : listing.community.virtualShowingUrl ?? null;
      let microsoftBookingServiceId = listing.microsoftBookingServiceId ?? null;

      let readyForShowingOn = null;
      if (listing.readyForShowingOn) {
        readyForShowingOn = listing.readyForShowingOn;
      } else if (listing.activeUnit) {
        if (listing.activeUnit.readyForShowingOn) {
          readyForShowingOn = listing.activeUnit.readyForShowingOn;
        } else if (listing.activeUnit.availableOn) {
          const availableOn = new Date(listing.activeUnit.availableOn);
          const sevenDaysBeforeAvailableOn = new Date(availableOn.getTime());
          sevenDaysBeforeAvailableOn.setUTCDate(availableOn.getUTCDate() - 7);
          readyForShowingOn = sevenDaysBeforeAvailableOn.toISOString();
        }
      }

      return {
        id: listing.id,
        listed_rent: listing.marketRent,
        address1: listing.activeUnit ? listing.activeUnit.address1 : listing.address1,
        address2: listing.activeUnit ? listing.activeUnit.address2 : listing.address2 ?? null,
        city: listing.city,
        state: listing.state,
        zip: listing.zip,
        latitude: listing.latitude ?? null,
        longitude: listing.longitude ?? null,
        application_url: listing.applicationUrl,
        application_fee: listing.applicationFee,
        available_on: listing.availableOn,
        bathrooms: listing.bathrooms,
        bedrooms: listing.bedrooms,
        square_feet: listing.squareFeet ?? null,
        showing_url: showingUrl,
        self_showing_url: listing.lockbox && listing.rentlyId ? `https://secure.rently.com/properties/schedule_embed?pid=${listing.rentlyId}` : null,
        virtual_tour_url: listing.virtualTourUrl ?? null,
        virtual_showing_url: virtualShowingUrl,
        marketing_title: listing.marketingTitle ?? null,
        unit_photos: (listing.photos ?? [])
          .map((photo) => {
            return {
              url: photo.url,
              thumbnail_url: photo.thumbnailUrl ?? photo.thumbnail_url ?? photo.url,
              sort_order: photo.sortOrder ?? null,
            };
          })
          .sort((a, b) => {
            return (a.sort_order ?? 0) - (b.sort_order ?? 0);
          }),
        promotions: listing.promotions ?? [],
        ready_for_showing_on: readyForShowingOn,
        microsoft_booking_service_id: microsoftBookingServiceId,
        community: listing.community ? {
          id: listing.community.id,
          showing_url: listing.community.showingUrl,
          virtual_showing_url: listing.community.virtualShowingUrl,
          microsoft_booking_service_id: listing.community.microsoftBookingServiceId,
        } : null,
      };
    });
  } catch {
    return [];
  }
};

onBeforeMount(async () => {
  const posted = await getListings();
  postedUnits.value = posted;

  loadingUnits.value = false;
  if (!postedUnits.value.length) {
    loadJotform();
  }
});

function loadJotform() {
  let iframe;
  var ifr = document.getElementById(`JotFormIFrame-${$maxxpm.config.availability.waitingListJotformId}`);
  if (ifr) {
    var src = ifr.src;
    var iframeParams = [];
    if (window.location.href && window.location.href.indexOf("?") > -1) {
      iframeParams = iframeParams.concat(window.location.href.substr(window.location.href.indexOf("?") + 1).split("&"));
    }
    if (src && src.indexOf("?") > -1) {
      iframeParams = iframeParams.concat(src.substr(src.indexOf("?") + 1).split("&"));
      src = src.substr(0, src.indexOf("?"));
    }
    iframeParams.push("isIframeEmbed=1");
    ifr.src = src + "?" + iframeParams.join("&");
  }
  window.handleIFrameMessage = function (e) {
    if (typeof e.data === "object") {
      return;
    }
    var args = e.data.split(":");
    if (args.length > 2) {
      iframe = document.getElementById("JotFormIFrame-" + args[args.length - 1]);
    } else {
      iframe = document.getElementById("JotFormIFrame");
    }
    if (!iframe) {
      return;
    }
    switch (args[0]) {
      case "scrollIntoView":
        iframe.scrollIntoView();
        break;
      case "setHeight":
        iframe.style.height = args[1] + "px";
        if (!isNaN(args[1]) && parseInt(iframe.style.minHeight) > parseInt(args[1])) {
          iframe.style.minHeight = args[1] + "px";
        }
        break;
      case "collapseErrorPage":
        if (iframe.clientHeight > window.innerHeight) {
          iframe.style.height = window.innerHeight + "px";
        }
        break;
      case "reloadPage":
        window.location.reload();
        break;
      case "loadScript":
        if (!window.isPermitted(e.origin, ["jotform.com", "jotform.pro"])) {
          break;
        }
        var src = args[1];
        if (args.length > 3) {
          src = args[1] + ":" + args[2];
        }
        var script = document.createElement("script");
        script.src = src;
        script.type = "text/javascript";
        document.body.appendChild(script);
        break;
      case "exitFullscreen":
        if (window.document.exitFullscreen) window.document.exitFullscreen();
        else if (window.document.mozCancelFullScreen) window.document.mozCancelFullScreen();
        else if (window.document.mozCancelFullscreen) window.document.mozCancelFullScreen();
        else if (window.document.webkitExitFullscreen) window.document.webkitExitFullscreen();
        else if (window.document.msExitFullscreen) window.document.msExitFullscreen();
        break;
    }
    var isJotForm = e.origin.indexOf("jotform") > -1 ? true : false;
    if (isJotForm && "contentWindow" in iframe && "postMessage" in iframe.contentWindow) {
      var urls = {
        docurl: encodeURIComponent(document.URL),
        referrer: encodeURIComponent(document.referrer),
      };
      iframe.contentWindow.postMessage(JSON.stringify({ type: "urls", value: urls }), "*");
    }
  };
  window.isPermitted = function (originUrl, whitelisted_domains) {
    var url = document.createElement("a");
    url.href = originUrl;
    var hostname = url.hostname;
    var result = false;
    if (typeof hostname !== "undefined") {
      whitelisted_domains.forEach(function (element) {
        if (hostname.slice(-1 * element.length - 1) === ".".concat(element) || hostname === element) {
          result = true;
        }
      });
      return result;
    }
  };
  if (window.addEventListener) {
    window.addEventListener("message", window.handleIFrameMessage, false);
  } else if (window.attachEvent) {
    window.attachEvent("onmessage", window.handleIFrameMessage);
  }
}
</script>

<style scoped>
.listings-container {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
  width: 100%;
  padding: 1.5rem 0 2rem;
  align-items: stretch;
}

.listing-card:only-child {
  grid-column: 1 / -1;
  width: 100%;
  max-width: calc((100% - 1.5rem) / 2);
  margin-inline: auto;
}

@media (max-width: 768px) {
  .listings-container {
    grid-template-columns: 1fr;
  }

  .listing-card:only-child {
    max-width: 100%;
  }
}

.units-loading {
  color: var(--mdb-secondary);
  font-size: 3rem;
  margin: 3rem 0;
}

.container {
  width: 95vw;
  max-width: 1200px;
  margin: auto;
}

.listing-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.listing-card :deep(.listing) {
  margin: 0;
  flex: 1 1 auto;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.listing-card :deep(.unit-listing) {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  border: none;
  border-radius: 0;
  background: #fafafa;
}

.listing-card :deep(.details-container),
.listing-card :deep(.address-container),
.listing-card :deep(.img-container),
.listing-card :deep(.unit-title),
.listing-card :deep(.availability-container) {
  flex: 0 0 auto;
}

.listing-card :deep(.btns-container) {
  margin-top: auto;
  flex-shrink: 0;
}

.listing-card :deep(.unit-img) {
  width: 100%;
  max-width: 100%;
  height: auto;
  display: block;
}

.listing-card :deep(.img-container) {
  width: 100%;
}

.listing-card-top {
  flex-shrink: 0;
}

.listing-card-top-spacer {
  min-height: 3.25rem;
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  background-color: #fafafa;
}

.promotion-banner {
  background: var(--primary-color);
  color: #fff;
  padding: 0.85rem 1rem;
  border-bottom: 4px solid #000;
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
}

.promotion-banner-title {
  margin: 0 0 0.5rem;
  font-size: 0.9rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.promotion-banner-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.35rem;
}

.promotion-banner-item:first-of-type {
  margin-top: 0;
}

.promotion-banner-item p {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.promotion-icon {
  flex-shrink: 0;
  margin-top: 0.15rem;
}
</style>
